import React, { useEffect, useRef, useState } from "react";
import Card from "components/card";

import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { IoEye } from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import UserService from "services/user.service";
import Loader from "../../../assets/img/loader.gif";
import Toast from "components/toaster";
import DateRangePicker from "components/fields/DateRangePicker";
import moment from "moment";
import { FiSearch } from "react-icons/fi";
import Modal from "components/modal/Modal";
import PostService from "services/post.service";
import SocialMediaCard from "components/card/SocialMediaCard";
import GooglePagination from "components/pagination/GooglePagination";

const columnHelper = createColumnHelper();

const SocialPosts = () => {
  const navigate = useNavigate();
  const profileRef = useRef();
  const activateRef = useRef();
  const [sorting, setSorting] = React.useState([]);
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [popupOpen, setPopupOpen] = useState(false);
  const [profilePopup, setProfilePopup] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isViewLoading, setIsViewLoading] = useState(false);
  const [id, setId] = useState("");
  const [confirmLoader, setConfirmLoader] = useState(false);
  const [value, setValue] = useState({
    startDate: null,
    endDate: null,
  });
  const [searchText, setSearchText] = useState("");
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const getAllPosts = async () => {
    setIsLoading(true);
    try {
      const body = {
        pagination: {
          page: currentPage,
          pageSize: pageSize,
          projection: {},
          filters: {
            // startDate: moment(value.startDate).toISOString(),
            // endDate: moment(value.endDate).toISOString(),
          },
          sort: { createdAt: -1 },
        },
        searchtext: searchText,
      };
      const response = await PostService.getAllPosts(body);
      // Sort posts by date in descending order (newest first)
      const posts = response.data.data || [];
      
      // Debug: Log first post to see available fields
      if (posts.length > 0) {
        console.log("Sample post data:", posts[0]);
        console.log("Available date fields:", {
          createdAt: posts[0].createdAt,
          created_at: posts[0].created_at,
          date: posts[0].date,
          created: posts[0].created,
          _id: posts[0]._id
        });
      }
      
      // Helper function to get timestamp from post
      const getTimestamp = (post) => {
        // Try multiple date field names (check all possible variations)
        const dateValue = post.createdAt || post.created_at || post.date || post.created || 
                         post.createdDate || post.created_date || post.timestamp || post.time;
        
        if (dateValue) {
          const timestamp = new Date(dateValue).getTime();
          if (!isNaN(timestamp) && timestamp > 0) {
            return timestamp;
          }
        }
        
        // Fallback: Extract timestamp from MongoDB ObjectId (first 4 bytes)
        // ObjectId format: timestamp (4 bytes) + machine (3 bytes) + pid (2 bytes) + counter (3 bytes)
        if (post._id) {
          const idString = typeof post._id === 'string' ? post._id : String(post._id);
          if (idString.length === 24) {
            try {
              const timestampHex = idString.substring(0, 8);
              const timestamp = parseInt(timestampHex, 16) * 1000; // Convert to milliseconds
              if (!isNaN(timestamp) && timestamp > 0) {
                return timestamp;
              }
            } catch (e) {
              console.error("Error parsing ObjectId timestamp:", e);
            }
          }
        }
        
        // Last resort: return a very old date so items without dates go to bottom
        return 0;
      };
      
      // Sort posts - prioritize date fields, fallback to _id
      const sortedData = [...posts].sort((a, b) => {
        const timestampA = getTimestamp(a);
        const timestampB = getTimestamp(b);
        
        // If both have valid timestamps, sort by timestamp
        if (timestampA > 0 && timestampB > 0) {
          return timestampB - timestampA; // Descending: newest first
        }
        
        // If only one has a timestamp, prioritize it
        if (timestampA > 0 && timestampB === 0) return -1;
        if (timestampA === 0 && timestampB > 0) return 1;
        
        // Fallback: Sort by _id timestamp extraction (more reliable than lexicographic)
        if (a._id && b._id) {
          const idA = String(a._id);
          const idB = String(b._id);
          
          // Extract timestamp from ObjectId hex string
          if (idA.length === 24 && idB.length === 24) {
            try {
              const timestampA = parseInt(idA.substring(0, 8), 16) * 1000;
              const timestampB = parseInt(idB.substring(0, 8), 16) * 1000;
              return timestampB - timestampA; // Descending: newest first
            } catch (e) {
              // If extraction fails, fall back to lexicographic comparison
              return idB.localeCompare(idA);
            }
          }
          
          // If not standard ObjectId format, use lexicographic comparison
          return idB.localeCompare(idA);
        }
        
        return 0;
      });
      
      // Debug: Log sorted order
      if (sortedData.length > 0) {
        console.log("First 3 posts after sorting:", sortedData.slice(0, 3).map(p => ({
          _id: p._id,
          date: p.createdAt || p.created_at,
          timestamp: getTimestamp(p)
        })));
      }
      setData(sortedData);
      setTotalCount(response.data.totalCount || 0);
      setTotalPages(Math.ceil((response.data.totalCount || 0) / pageSize));
    } catch (error) {
      Toast("error", error.response?.data?.message || "Failed to fetch posts");
    } finally {
      setIsLoading(false);
    }
  };
  
  useEffect(() => {
    getAllPosts();
    window.scrollTo(0, 0);
  }, [currentPage, pageSize, searchText, value.startDate, value.endDate]);

  const handleSearch = (e) => {
    setSearchText(e.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    window.scrollTo(0, 0); // Scroll to top when changing pages
  };

  const handleOpen = async (item) => {
    setIsViewLoading(true);
    setId(item);
    try {
      const body = {
        id: item,
      };
      const response = await UserService.getUserDetails(body);
      setProfilePopup(true);
      document.body.style.overflow = "hidden";
    } catch (error) {
      Toast("error", error.response.data.message);
    } finally {
      setIsViewLoading(false);
      setId("");
    }
  };

  const handleOpenAvtive = async (id, active) => {
    setSelectedUser(active.original.is_active);
    setId(id);
    setPopupOpen(true);
    document.body.style.overflow = "hidden";
  };

  const handleToggleActivation = async () => {
    setConfirmLoader(true);
    try {
      const body = {
        user_id: id,
        is_active: !selectedUser,
      };
      const response = await UserService.activationUser(body);
      setPopupOpen(false);
      document.body.style.overflow = "unset";
      Toast("success", response.data.message);
    } catch (error) {
      Toast("error", error.response.data.message);
    } finally {
      setConfirmLoader(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setProfilePopup(false);
        document.body.style.overflow = "unset";
      }
      if (activateRef.current && !activateRef.current.contains(event.target)) {
        setPopupOpen(false);
        document.body.style.overflow = "unset";
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const columns = [
    columnHelper.accessor("full_name", {
      id: "full_name",
      header: () => (
        <p className="text-sm font-bold uppercase text-white">Full Name</p>
      ),
      cell: (info) => (
        <p className="text-sm font-bold text-white">
          {info.getValue() ? info.getValue() : "-"}
        </p>
      ),
    }),
    columnHelper.accessor("phone", {
      id: "phone",
      header: () => (
        <p className="text-sm font-bold uppercase text-white">Mobile Number</p>
      ),
      cell: (info) => (
        <div className="flex items-center">
          <p className="text-sm font-bold text-white">
            {info.getValue() ? info.getValue() : "-"}
          </p>
        </div>
      ),
    }),
    columnHelper.accessor("is_verified", {
      id: "is_verified",
      header: () => (
        <p className="text-sm font-bold uppercase text-white">Verified</p>
      ),
      cell: (info) => (
        <p className="text-sm font-bold text-white">
          {info.getValue() ? "Yes" : "No"}
        </p>
      ),
    }),
    columnHelper.accessor("is_active", {
      id: "is_active",
      header: () => (
        <p className="text-sm font-bold uppercase text-white">Active</p>
      ),
      cell: (info) => (
        <div className="flex items-center">
          {info.getValue() ? "Yes" : "No"}
        </div>
      ),
    }),
    columnHelper.accessor("email", {
      id: "email",
      header: () => (
        <p className="text-sm font-bold uppercase text-white">Email</p>
      ),
      cell: (info) => (
        <div className="flex items-center">
          {info.getValue() ? info.getValue() : "--"}
        </div>
      ),
    }),
    columnHelper.accessor("is_active", {
      id: "is_active",
      header: () => (
        <p className="text-sm font-bold uppercase text-white">Action</p>
      ),
      cell: (info) => {
        const rowId = info.row.original._id;
        return (
          <div className="flex gap-3">
            <div
              className="ms-center m-0 flex w-28 cursor-pointer items-center justify-center rounded-md bg-primary-gradient px-1.5 text-sm font-bold text-white"
              onClick={() => handleOpenAvtive(rowId, info.row)}
            >
              {info.getValue() ? "Activated" : "Not Activated"}
            </div>
          </div>
        );
      },
    }),
  ];

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    debugTable: true,
  });

  return (
    <div className={`${isLoading && "m-auto h-screen"}`}>
      <div className="mt-3 grid h-full grid-cols-1">
        <div className="col-span-1 h-full w-full xl:col-span-1 min-w-0">
          <div className="flex justify-between gap-3 mb-4">
            <div className="mt-2 md:mt-4 flex h-fit justify-start">
              <div className="flex items-center rounded-full bg-navy-800 py-2 h-[40px] text-white xl:w-[170px]">
                <p className="pl-3 pr-2 text-xl">
                  <FiSearch className="h-4 w-4 text-white" />
                </p>
                <input
                  type="text"
                  placeholder="Search..."
                  className="block w-[90px] max-w-[90px] bg-navy-800 text-sm font-medium text-white outline-none placeholder:!text-white"
                  onChange={handleSearch}
                />
              </div>
            </div>
          </div>
          {isLoading ? (
            <div 
              className="fixed" 
              style={{ 
                top: '50vh', 
                left: '57vw', 
                transform: 'translate(-50%, -50%)', 
                zIndex: 40,
                margin: 0,
                padding: 0,
                width: 'auto',
                height: 'auto'
              }}
            >
              <img
                src={Loader}
                alt="Loading..."
                className="h-16 w-16 block"
                style={{ margin: '0 auto', padding: 0, display: 'block' }}
              />
            </div>
          ) : (
            <>
              <div className="grid h-full grid-cols-1 gap-5">
                <div className="xs:grid-cols-2 z-20 grid grid-cols-1 gap-5 md:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-5">
                  {data?.map((post) => (
                    <SocialMediaCard
                      key={post._id}
                      title={post?.account?.name}
                      description={post?.description}
                      price={post?.likecount}
                      comments={post?.likecount}
                      image={post}
                      getAllPosts={getAllPosts}
                    />
                  ))}
                </div>
                
                {/* Google-style Pagination */}
                {!isLoading && data.length > 0 && totalPages > 1 && (
                  <div className="mt-3 pt-3">
                    <GooglePagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      totalCount={totalCount}
                      pageSize={pageSize}
                      onPageChange={handlePageChange}
                      className="w-full"
                    />
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SocialPosts;
